import type { StreamingBlock } from "@storytell/streaming";
import { determinePreferredRenderer, getBlockContent } from "@storytell/streaming";
import { TbAlertTriangle } from "solid-icons/tb";
import { Show } from "solid-js";

interface UnknownRendererProps {
  block: StreamingBlock;
}

/**
 * Fallback renderer for blocks with unknown or unspecified message types.
 * Provides basic display with a clear indication that the renderer is unknown.
 */
export const UnknownRenderer = (props: UnknownRendererProps) => {
  const content = () => {
    // Try finalContent first (for completed blocks)
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      return props.block.finalContent.text;
    }
    // Fallback to streaming content from granular store
    return getBlockContent(props.block.blockId);
  };

  return (
    <div
      data-renderer="unknown-renderer"
      class="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950"
    >
      <div class="flex items-start gap-3">
        <div class="mt-1 shrink-0">
          <TbAlertTriangle size={16} class="text-amber-600 dark:text-amber-400" />
        </div>

        <div class="min-w-0 flex-1">
          <div class="mb-2 text-sm font-medium text-amber-800 dark:text-amber-200">Unknown Block Type</div>

          <div class="mb-3 text-xs text-amber-600 dark:text-amber-300">
            MessageType: {props.block.messageType} | Renderer: {determinePreferredRenderer(props.block)}
          </div>

          <Show when={props.block.title}>
            <div class="mb-2 text-sm font-medium text-amber-700 dark:text-amber-300">{props.block.title}</div>
          </Show>

          <Show when={props.block.activity}>
            <div class="mb-2 text-xs text-amber-600 italic dark:text-amber-400">Activity: {props.block.activity}</div>
          </Show>

          <Show when={content().trim()}>
            <div class="rounded border border-amber-200 bg-white p-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900 dark:text-amber-200">
              <div class="text-xs whitespace-pre-wrap">{content()}</div>
            </div>
          </Show>

          <Show when={!props.block.isComplete}>
            <div class="mt-3 flex items-center gap-2">
              <div class="size-2 animate-pulse rounded-full bg-amber-500" />
              <span class="text-xs text-amber-600 dark:text-amber-300">Processing...</span>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
};
